import { useEffect, Fragment } from "react";

/* eslint-disable complexity */
import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { LoadingButton } from "@atlaskit/button";
import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives/responsive";
import Spinner from "@atlaskit/spinner";

import { AisSitesType } from "@/libraries/bxp-web-stargate-client/src/index";
import { Logo } from "@/libraries/wpl/foundations/src";

import { useAnalyticsClient } from "../../../../../analytics/wac-analytics/analytics-client";

const START_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://start.atlassian.com"
        : "https://start.stg.atlassian.com";

const wrapLogMeSecAndLines = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    [media.only.md]: {
        padding: "0 9.5rem"
    },
    [media.only.sm]: {
        padding: "0 6rem"
    },
    [media.only.xs]: {
        padding: "0 2rem"
    },
    [media.only.xxs]: {
        padding: "0 1rem"
    }
});

const logMeBackInSectionStyle = css({
    margin: `88px auto 56px auto`,
    width: "100%",
    maxWidth: 850,
    padding: "24px 0px 24px 0px",
    [media.below.sm]: {
        flexDirection: "column"
    },
    [media.above.sm]: {
        flexDirection: "row"
    }
});

const headingStyle = css({
    fontSize: "48px",
    lineHeight: "56px",
    fontWeight: "500",
    marginBlockStart: "0px",
    marginBlockEnd: "8px"
});

const subHeadingStyle = css({
    fontSize: "20px",
    marginBlockEnd: "24px",
    marginBlockStart: "0px"
});

const confluenceSiteBoxStyle = css({
    position: "relative",
    zIndex: 1,
    width: "auto",
    height: "72px",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
    padding: "16px 24px 16px 16px",
    backgroundColor: "#ffff",
    [media.below.xs]: {
        flexDirection: "column",
        height: "auto",
        alignItems: "start",
        padding: "7px 16px 16px"
    }
});

const logoAndSiteNameStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "0px"
});
const logoStyle = css({
    marginInlineEnd: "10px",
    marginBlockStart: "9px"
});

const siteStyle = css({
    lineHeight: "22px",
    fontSize: "16px",
    overflow: "hidden"
});

const productStyle = css({
    fontWeight: "500",
    height: "22px",
    fontSize: "17px",
    textAlign: "start"
});

const siteNameStyle = css({
    fontWeight: "300",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    height: "22px"
});

const buttonStyle = css({
    marginInlineStart: "15px",
    [media.below.xs]: {
        marginInlineStart: 0,
        width: "100%"
    }
});

const otherConfluenceSiteButton = css({
    paddingInlineStart: "0px !important",
    marginBlockStart: "12px"
});

const otherConfluenceSiteStyle = css({
    marginBlockStart: "15px",
    fontSize: "16px",
    textDecoration: "underline",
    fontWeight: "400px"
});

export enum TRACKING_EVENT_NAMES {
    loggedInHeroProductCtaClicked = "loggedInHeroProductCtaClicked",
    loggedInHeroAnotherSiteCtaClicked = "loggedInHeroAnotherSiteCtaClicked"
}

export const loggedInHeroAnalyticsPayload = {
    eventType: "ui",
    action: "clicked",
    actionSubject: "button",
    actionSubjectId: "loggedInHero",
    source: "/software/confluence"
};

export const loggedInHeroLoadedAnalyticsPayload = {
    eventType: "ui",
    action: "loaded",
    actionSubject: "page",
    actionSubjectId: "loggedInHero",
    source: "/software/confluence",
    attributes: {
        atlasId: "CFIND-1225"
    }
};

interface LogMeInSectionProps {
    confluenceSites: AisSitesType[];
    userProfileName: string | undefined;
    loading: boolean;
}

export const LogMeInSection = ({
    confluenceSites,
    userProfileName,
    loading
}: LogMeInSectionProps) => {
    const createConfluenceUrl = (url: string) => {
        try {
            const tempUrl = new URL(url);
            tempUrl.pathname = "/wiki";
            tempUrl.search = "";
            return tempUrl.toString();
        } catch {
            return url;
        }
    };

    const getSitenameFromUrl = (url: string) => {
        try {
            const tempUrl = new URL(url);
            return tempUrl.hostname.split(".")[0] || "";
        } catch {
            return "";
        }
    };

    const firstName = userProfileName?.split(" ")[0];
    const siteUrl =
        createConfluenceUrl(confluenceSites[0]?.url || "") || START_URL;
    const siteName = getSitenameFromUrl(confluenceSites[0]?.url || "");
    const hasMultipleConfluences = confluenceSites.length > 1;

    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        if (typeof window !== "undefined") {
            createAnalyticsEvent({
                ...loggedInHeroLoadedAnalyticsPayload
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent]);

    const clickHandler = (eventName: TRACKING_EVENT_NAMES) => {
        createAnalyticsEvent({
            ...loggedInHeroAnalyticsPayload,
            attributes: {
                eventName,
                url: window.location.href,
                atlasId: "CFIND-1225"
            }
        }).fire();
    };

    return (
        <Fragment>
            <section>
                <div css={wrapLogMeSecAndLines}>
                    <div css={logMeBackInSectionStyle}>
                        <h1 css={headingStyle}>
                            {firstName ? (
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.loggedInHero.welcomeBackWithName"
                                    defaultMessage={`Welcome back, {name}.`}
                                    values={{ name: firstName }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.loggedInHero.welcomeBack"
                                    defaultMessage="Welcome back"
                                />
                            )}
                        </h1>
                        <p css={subHeadingStyle}>
                            <FormattedMessage
                                id="ConfluenceSoftwarePageTemplate.loggedInHero.pickUpWhereYouLeftOff"
                                defaultMessage="Pick up where you left off in Confluence."
                            />
                        </p>

                        <div css={confluenceSiteBoxStyle}>
                            <div css={logoAndSiteNameStyle}>
                                <div css={logoStyle}>
                                    <Logo
                                        brand="confluence"
                                        type="mark"
                                        appearance="brand"
                                    />
                                </div>
                                <div css={siteStyle}>
                                    <div css={productStyle}>Confluence</div>
                                    {loading ? (
                                        <Spinner size="small" />
                                    ) : (
                                        <div css={siteNameStyle}>
                                            {siteName}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <LoadingButton
                                id="confluence-button"
                                appearance="warning"
                                css={buttonStyle}
                                href={siteUrl}
                                isLoading={loading}
                                onClick={() =>
                                    clickHandler(
                                        TRACKING_EVENT_NAMES.loggedInHeroProductCtaClicked
                                    )
                                }
                            >
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.loggedInHero.goToConfluence"
                                    defaultMessage="Go to Confluence"
                                />
                            </LoadingButton>
                        </div>

                        {hasMultipleConfluences && (
                            <Button
                                css={otherConfluenceSiteButton}
                                href={START_URL}
                                appearance="subtle-link"
                                onClick={() =>
                                    clickHandler(
                                        TRACKING_EVENT_NAMES.loggedInHeroAnotherSiteCtaClicked
                                    )
                                }
                            >
                                <span css={otherConfluenceSiteStyle}>
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePageTemplate.loggedInHero.lookingForAnotherSite"
                                        defaultMessage="I’m looking for another Confluence site"
                                    />
                                </span>
                            </Button>
                        )}
                    </div>
                </div>
            </section>
        </Fragment>
    );
};

export default LogMeInSection;
